'use client';

import { useEffect, useMemo, useState, type JSX } from 'react';
import {
  Button,
  Chip,
  Input,
  Slider,
  Accordion,
  AccordionItem,
  CheckboxGroup,
  Checkbox,
} from '@heroui/react';
import { Icon } from '@iconify/react';
import { VehicleFilters as VehicleFiltersType } from '@/utils/types';
import useVehicleFiltersStore from '@/store/useVehicleFiltersStore';
import { useTranslation } from '@/i18n/hooks/useTranslation';
import { useCurrency } from '@/hooks/useCurrency';
import useClientStore from '@/store/useClientStore';
import { filtroFacturableDisponible } from '@/lib/facturable';
import { etiquetaCatalogo, type TipoValorCatalogo } from '@/utils/functions';
import { forcedSiteLanguage } from '@/lib/country';

// -------- Traducción con fallback humano --------
function useTx() {
  const { t } = useTranslation();
  const tx = (key: string, fallback: string) => {
    const val = t?.(key) ?? '';
    if (!val || val === key) return fallback;
    return val;
  };
  return { tx };
}

// --- helpers de seguridad para listas / strings / números ---
const safeList = <T,>(val: T[] | undefined | null): T[] => (Array.isArray(val) ? val : []);
const safeStr = (s: any) => (typeof s === 'string' ? s : '');
const isFiniteNum = (n: any) => typeof n === 'number' && Number.isFinite(n);

/**
 * Cuántas opciones de Marca y Modelo se muestran de entrada. Una automotora
 * grande tiene 40 marcas y 300 modelos: la lista completa convertía el
 * acordeón en un muro y empujaba el resto de los filtros fuera de la vista.
 * Con el buscador escrito se muestran TODAS las coincidencias, sin tope.
 */
const LIMITE_LISTA = 10;

type IdName = {
  id: number | string;
  name: string;
  hex?: string;
  brand_id?: string | number;
  brand?: { id: any };
};

interface Props {
  brands?: IdName[];
  models?: IdName[];
  categories?: IdName[];
  fuelTypes?: IdName[];
  conditions?: IdName[];
  colors?: IdName[];
  /** Sucursales con stock ({id, name} ya resuelto). Con 0 o 1 la sección no se muestra. */
  dealerships?: IdName[];
  /** Cuántos vehículos se venden con factura afecta a IVA y cuántos no. */
  facturableCounts?: { si: number; no: number };
  /**
   * Conteo por slug de transmisión y de tracción, ya normalizados por quien
   * llama (`countTransmissions`/`countTractions` de `@/lib/vehicleSpecs`). Sin
   * ellos el acordeón no se dibuja, igual que Facturable.
   */
  transmissionCounts?: { id: string; count: number }[];
  tractionCounts?: { id: string; count: number }[];
  availableYears?: (string | number)[];
  maxPrice?: number;
  initialOpenAccordion?: string;
  /** Color CTA hex o css-var (p.ej. "#FF5C00" o "var(--brand-color)") */
  ctaColor?: string;
  /** Si el panel está colapsado */
  isCollapsed?: boolean;
  /** Callback para colapsar/expandir */
  onToggleCollapse?: () => void;
  /** Override de "Limpiar filtros" (ej. para también limpiar ?promo= de la URL) */
  onClearFilters?: () => void;
  /**
   * Omite el encabezado propio (título, contador, "Limpiar"). Lo usa el drawer
   * del celular, que ya trae un encabezado con esos mismos controles: con los
   * dos se veía "Filtros" dos veces, uno debajo del otro.
   */
  sinEncabezado?: boolean;
}

const NewVehicleFilters = ({
  brands,
  models,
  categories,
  fuelTypes,
  conditions,
  colors,
  dealerships,
  facturableCounts,
  transmissionCounts,
  tractionCounts,
  availableYears = [],
  maxPrice = 1_000_000_000,
  initialOpenAccordion,
  ctaColor,
  isCollapsed,
  onToggleCollapse,
  onClearFilters,
  sinEncabezado = false,
}: Props) => {
  const { tx } = useTx();
  // País del tenant: Facturable (IVA chileno) no existe en Brasil, y allá los
  // valores de catálogo de la base ("semi-nuevo", "gasolina"…) se muestran en
  // portugués. Cualquier otro país: lo de siempre.
  const { client } = useClientStore();
  const country = client?.country;
  const conFacturable = filtroFacturableDisponible(country);
  // EE.UU.: los buscadores de marca/modelo, en inglés (el resto, como siempre),
  // y el slider de precio con paso de 1.000: el de 1 millón es para pesos y
  // con dólares no se mueve.
  const enUsa = forcedSiteLanguage(country) === 'en';
  /** Etiqueta VISIBLE de una opción de catálogo; el id/valor que se filtra no cambia. */
  const etiqueta = (tipo: TipoValorCatalogo, nombre: string) =>
    etiquetaCatalogo(tipo, nombre, country, nombre);
  const {
    filters,
    setFilters,
    priceRange,
    setPriceRange,
    clearFilters,
    searchQuery,
  } = useVehicleFiltersStore();

  // ===== LISTAS SEGURAS (evita undefined en plantillas "apagadas") =====
  const BRANDS = safeList(brands);
  const MODELS = safeList(models);
  const CATEGORIES = safeList(categories);
  const FUELTYPES = safeList(fuelTypes);
  const CONDITIONS = safeList(conditions);
  const COLORS = safeList(colors);
  const DEALERSHIPS = safeList(dealerships);
  const YEARS = safeList(availableYears as any[]);
  const FACTURABLE = { si: facturableCounts?.si ?? 0, no: facturableCounts?.no ?? 0 };
  const TRANSMISSIONS = transmissionCounts ?? [];
  const TRACTIONS = tractionCounts ?? [];
  /** ¿Vale la pena mostrar el acordeón? Con todo en 0 no hay nada que filtrar. */
  const hayAlgo = (l: { count: number }[]) => l.some((o) => o.count > 0);

  // ===== Textos =====
  const filtersTitle = tx('vehicles.filters.title', 'Filtros');
  const clearFiltersText = tx('vehicles.filters.clearFilters', 'Limpiar filtros');
  const priceLabel = tx('vehicles.filters.priceRange', 'Rango de precio');
  const minPriceText = tx('vehicles.filters.minPrice', 'Mínimo');
  const maxPriceText = tx('vehicles.filters.maxPrice', 'Máximo');
  const showMoreText = tx('vehicles.filters.showMore', 'Ver más');
  const showLessText = tx('vehicles.filters.showLess', 'Ver menos');

  const brandText = tx('vehicles.filters.brand', 'Marca');
  const modelText = tx('vehicles.filters.model', 'Modelo');
  const categoryText = tx('vehicles.filters.category', 'Categoría');
  const fuelText = tx('vehicles.filters.fuelType', 'Combustible');
  const conditionText = tx('vehicles.filters.condition', 'Condición');
  const colorText = tx('vehicles.filters.color', 'Color');
  const yearText = tx('vehicles.filters.year', 'Año');
  const dealershipText = tx('vehicles.filters.dealership', 'Sucursal');
  const facturableText = tx('vehicles.filters.facturable', 'Facturable');
  const transmissionText = tx('vehicles.filters.transmission', 'Transmisión');
  const tractionText = tx('vehicles.filters.traction', 'Tracción');
  /** Rótulo por slug. Los slugs son los de `@/lib/vehicleSpecs`. */
  const SPEC_LABELS: Record<string, string> = {
    manual: tx('vehicles.filters.transmissionManual', 'Manual'),
    automatica: tx('vehicles.filters.transmissionAutomatic', 'Automática'),
    '4x2': tx('vehicles.filters.traction4x2', '4x2'),
    '4x4': tx('vehicles.filters.traction4x4', '4x4'),
    awd: tx('vehicles.filters.tractionAwd', 'AWD (integral)'),
    fwd: tx('vehicles.filters.tractionFwd', 'FWD (delantera)'),
  };
  const facturableYesText = tx('vehicles.filters.facturableYes', 'Con factura');
  const facturableNoText = tx('vehicles.filters.facturableNo', 'Sin factura');
  const FACTURABLE_OPTIONS: { id: 'si' | 'no'; label: string }[] = [
    { id: 'si', label: facturableYesText },
    { id: 'no', label: facturableNoText },
  ];

  // -------- helpers base --------
  const asArray = (v?: string | string[]) => (Array.isArray(v) ? v : v ? [v] : []);
  const setMulti = (key: keyof VehicleFiltersType, next: string[]) => {
    const nf: VehicleFiltersType = { ...filters };
    if (!next.length) delete (nf as any)[key];
    else (nf as any)[key] = next;
    setFilters(nf);
  };
  const getName = (list: IdName[], id: string) =>
    list.find((i) => String(i?.id) === String(id))?.name || String(id);

  const [openAccordion, setOpenAccordion] = useState<string | null>(initialOpenAccordion || null);

  // ====== AÑO: rango Desde/Hasta sobre el mismo formato del store ======
  // `filters.year` sigue siendo la lista de años sueltos que ya consumen el
  // filtrado, la URL y el contador de filtros activos. Acá sólo cambia la UI:
  // se leen el mínimo y el máximo de esa lista y se vuelve a expandir el rango
  // al guardar. Antes eran ~30 checkboxes en una grilla de 3 columnas, que en
  // mobile obligaban a tildar año por año para pedir "de 2018 en adelante".
  const YEAR_OPTIONS = useMemo(
    () =>
      YEARS.map((y) => Number(y))
        .filter((y) => Number.isFinite(y))
        .sort((a, b) => b - a),
    [YEARS]
  );
  const selectedYears = asArray(filters.year)
    .map((y) => Number(y))
    .filter((y) => Number.isFinite(y));
  const yearFrom = selectedYears.length ? Math.min(...selectedYears) : null;
  const yearTo = selectedYears.length ? Math.max(...selectedYears) : null;

  /** Expande [desde, hasta] a la lista de años disponibles dentro del rango. */
  const setYearRange = (from: number | null, to: number | null) => {
    if (from === null && to === null) return setMulti('year', []);
    // Elegir un "hasta" menor que el "desde" (o al revés) da un rango vacío y
    // el listado se queda sin resultados sin explicación: se ordenan solos.
    const lo = from ?? Math.min(...YEAR_OPTIONS);
    const hi = to ?? Math.max(...YEAR_OPTIONS);
    const [a, b] = lo <= hi ? [lo, hi] : [hi, lo];
    setMulti(
      'year',
      YEAR_OPTIONS.filter((y) => y >= a && y <= b).map(String)
    );
  };

  // `vehicles.filters.yearFrom/yearTo/anyYear` nunca existieron en ningún idioma
  // (siempre salía el respaldo): viven en catalog.filters, con el mismo español.
  const yearFromText = tx('catalog.filters.yearFrom', 'Desde');
  const yearToText = tx('catalog.filters.yearTo', 'Hasta');
  const anyYearText = tx('catalog.filters.anyYear', 'Cualquiera');
  /** `h-11` = 44px: mínimo táctil de iOS. El `<select>` es nativo a propósito
      — en celular abre el picker del sistema, sin capas ni z-index de por medio. */
  const YEAR_SELECT_CLASS =
    'w-full h-11 rounded-lg border border-slate-200 dark:border-dark-border bg-white dark:bg-dark-bg px-2 text-sm text-gray-900 dark:text-white appearance-none';
  /** Campos de precio: ver el comentario sobre el tamaño en el primer `<Input>`. */
  const PRICE_INPUT_CLASSES = {
    input: 'text-[13px] font-medium text-gray-900 dark:text-white tabular-nums min-w-0',
    inputWrapper: 'h-11 min-h-11 px-2',
  };

  // ====== priceRange seguro (por si llega undefined / NaN) ======
  const PR: [number, number] = useMemo(() => {
    const a = Array.isArray(priceRange) ? priceRange : [0, maxPrice];
    const min = isFiniteNum(a[0]) ? a[0] : 0;
    const max = isFiniteNum(a[1]) ? a[1] : maxPrice;
    const clampedMin = Math.max(0, Math.min(min, max));
    const clampedMax = Math.min(Math.max(max, clampedMin), maxPrice);
    return [clampedMin, clampedMax];
  }, [priceRange, maxPrice]);

  const { formatPrice, locale } = useCurrency();
  const formatCLP = (n: number) => formatPrice(isFinite(n) ? n : 0);
  /**
   * Miles con separador, sin símbolo: es lo que va DENTRO de los campos de
   * precio (el símbolo ya está como prefijo). Antes el campo mostraba
   * "12000000" pelado, que a simple vista no se distingue de "1200000".
   */
  const formatMiles = (n: number) =>
    new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(isFinite(n) ? n : 0);
  const parseNumber = (s: string) => Number((s || '0').replace(/\D/g, '')) || 0;
  /** Lo que se teclea se reformatea al vuelo: sólo dígitos, con sus puntos. */
  const formatTecleado = (s: string) => {
    const digits = s.replace(/\D/g, '');
    return digits ? formatMiles(Number(digits)) : '';
  };

  // input precio controlado
  const [minInput, setMinInput] = useState<string>(formatMiles(0));
  const [maxInput, setMaxInput] = useState<string>(formatMiles(maxPrice));
  useEffect(() => {
    setMinInput(formatMiles(PR[0] || 0));
    setMaxInput(formatMiles(PR[1] || maxPrice));
  }, [PR, maxPrice, locale]);

  const commitMin = () => {
    const v = parseNumber(minInput);
    const clamped = Math.max(0, Math.min(v, PR[1]));
    setPriceRange([clamped, PR[1]]);
    setMinInput(formatMiles(clamped));
  };
  const commitMax = () => {
    const v = maxInput ? parseNumber(maxInput) : maxPrice;
    const clamped = Math.min(Math.max(v, PR[0]), maxPrice);
    setPriceRange([PR[0], clamped]);
    setMaxInput(formatMiles(clamped));
  };
  const resetPrice = () => {
    setPriceRange([0, maxPrice]);
    setMinInput(formatMiles(0));
    setMaxInput(formatMiles(maxPrice));
  };

  // modelos dependientes de marca si hay relación brand_id o model.brand.id
  const selectedBrandIds = asArray(filters.brand);
  const filteredModels: IdName[] = useMemo(() => {
    if (!selectedBrandIds.length) return MODELS;
    const set = new Set(selectedBrandIds.map(String));
    return MODELS.filter((m: any) => {
      const byField = m?.brand_id?.toString && set.has(String(m.brand_id));
      const byObj = m?.brand?.id?.toString && set.has(String(m.brand.id));
      return byField || byObj;
    });
  }, [MODELS, selectedBrandIds.join(',')]);

  // contador filtros activos
  // `year` cuenta 1 y no una unidad por año: la UI es un rango Desde/Hasta,
  // así que pedir 2018–2025 es UN filtro, no ocho. El resto sigue sumando por
  // opción elegida, que es lo que representan (tres marcas son tres filtros).
  // El ORDEN no cuenta: no acota la lista, sólo la acomoda. Contarlo hacía que
  // "Limpiar filtros" apareciera con la lista completa a la vista, y que el
  // botón flotante del celular mostrara "1" sin que hubiera nada que limpiar.
  const activeFiltersCount =
    Object.entries(filters).reduce(
      (acc, [key, v]) =>
        acc + (Array.isArray(v) ? (key === 'year' ? (v.length ? 1 : 0) : v.length) : v ? 1 : 0),
      0
    ) +
    (PR[0] > 0 || PR[1] < maxPrice ? 1 : 0) +
    (safeStr(searchQuery).trim() !== '' ? 1 : 0);

  // búsquedas locales para listas largas
  const [brandQuery, setBrandQuery] = useState('');
  const [modelQuery, setModelQuery] = useState('');

  // ====== MARCA y MODELO: diez de entrada, "Ver más" para el resto ======
  // Con el buscador escrito se muestran TODAS las coincidencias: quien busca
  // "toy" quiere ver Toyota aunque esté en el puesto 30. Lo elegido vive en el
  // store (`filters.brand`/`filters.model`), no en la lista visible, así que
  // expandir o contraer no toca las selecciones; los chips de abajo las
  // siguen mostrando aunque la opción haya quedado plegada.
  const [brandExpanded, setBrandExpanded] = useState(false);
  const [modelExpanded, setModelExpanded] = useState(false);

  const brandMatches = useMemo(() => {
    const q = brandQuery.trim().toLowerCase();
    return q ? BRANDS.filter((b) => safeStr(b?.name).toLowerCase().includes(q)) : BRANDS;
  }, [BRANDS, brandQuery]);
  const modelMatches = useMemo(() => {
    const q = modelQuery.trim().toLowerCase();
    return q
      ? filteredModels.filter((m) => safeStr(m?.name).toLowerCase().includes(q))
      : filteredModels;
  }, [filteredModels, modelQuery]);

  const acotar = <T,>(lista: T[], query: string, expandida: boolean) => {
    const sinTope = query.trim() !== '' || expandida;
    const visibles = sinTope ? lista : lista.slice(0, LIMITE_LISTA);
    return {
      visibles,
      /** Hay botón sólo sin búsqueda y con más opciones que el tope. */
      conBoton: query.trim() === '' && lista.length > LIMITE_LISTA,
      ocultas: lista.length - visibles.length,
    };
  };
  const marcas = acotar(brandMatches, brandQuery, brandExpanded);
  const modelos = acotar(modelMatches, modelQuery, modelExpanded);

  const CTA = ctaColor || undefined;

  /**
   * Botón "Ver más (N)" / "Ver menos" al pie de una lista acotada. Es una
   * función que devuelve JSX y no un componente declarado adentro del render:
   * un componente definido acá cambiaría de identidad en cada render y React
   * lo desmontaría y volvería a montar en cada tecla del buscador.
   */
  const botonVerMas = ({
    expandida,
    ocultas,
    onToggle,
    label,
  }: {
    expandida: boolean;
    ocultas: number;
    onToggle: () => void;
    label: string;
  }) => (
    <Button
      size="sm"
      variant="light"
      onPress={onToggle}
      className="mt-2 h-9 px-2 text-sm font-medium text-primary"
      style={CTA ? { color: CTA } : undefined}
      aria-expanded={expandida}
      aria-label={`${expandida ? showLessText : showMoreText} ${label}`}
      endContent={
        <Icon
          icon="mdi:chevron-down"
          className={`text-lg transition-transform ${expandida ? 'rotate-180' : ''}`}
        />
      }
    >
      {expandida ? showLessText : `${showMoreText} (${ocultas})`}
    </Button>
  );

  return (
    // Columna flex que se ENCOGE dentro de lo que le dé el contenedor: `min-h-0`
    // es lo que permite que un hijo flex baje de su alto de contenido; sin eso
    // el panel medía lo que mide la lista completa, el contenedor de afuera
    // era el que scrolleaba y el encabezado (la única vía a "Limpiar") se iba
    // con el scroll junto con los bordes y la sombra de la tarjeta. Ahora el
    // encabezado queda fijo y sólo scrollea la zona de acordeones, adentro.
    //
    // `h-full max-h-full` siguen por el drawer y el modal del celular, que le
    // dan al panel un alto definido y esperan que lo ocupe entero.
    <div
      className="w-full h-full max-h-full flex-1 min-h-0 flex flex-col bg-white dark:bg-dark-card rounded-2xl overflow-hidden border border-slate-200 dark:border-dark-border shadow-sm"
    >
      {/* HEADER — no scrollea: es la única vía a "Limpiar filtros" */}
      {!sinEncabezado && (
      <div className="shrink-0 px-4 py-3 border-b border-slate-100 dark:border-dark-border">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            {onToggleCollapse && (
              <button
                onClick={onToggleCollapse}
                className="p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-dark-bg transition-colors"
                aria-label={isCollapsed ? tx('catalog.filters.expand', 'Expandir filtros') : tx('catalog.filters.collapse', 'Colapsar filtros')}
              >
                <Icon
                  icon="mdi:chevron-left"
                  className={`text-xl text-gray-500 dark:text-gray-400 transition-transform duration-300 ${
                    isCollapsed ? 'rotate-180' : 'rotate-0'
                  }`}
                />
              </button>
            )}
            <Icon icon="solar:filter-linear" className="text-xl" style={CTA ? { color: CTA } : undefined} />
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">{filtersTitle}</h3>
            {activeFiltersCount > 0 && (
              <Chip size="sm" color="primary" variant="flat" className="font-semibold">
                {activeFiltersCount}
              </Chip>
            )}
          </div>

          {activeFiltersCount > 0 && (
            <Button
              size="sm"
              variant="light"
              color="danger"
              onPress={() => (onClearFilters ? onClearFilters() : clearFilters(maxPrice))}
              className="text-[13px] h-7 px-2"
              startContent={<Icon icon="mdi:filter-remove-outline" className="text-base" />}
            >
              {clearFiltersText}
            </Button>
          )}
        </div>
      </div>
      )}

      {/* CONTENIDO: se estira hasta el alto que le dé el contenedor y scrollea
          ahí adentro. `min-h-0` es lo que permite que un hijo flex encoja por
          debajo de su contenido; sin eso el scroll se lo lleva el padre.

          `overscroll-contain` SÓLO en el celular (`md:overscroll-auto`): ahí el
          panel vive en un drawer/modal encima de la página y, al llegar al
          final de la lista, el gesto no tiene que seguir moviendo lo de atrás.
          En escritorio es al revés: con la rueda del mouse sobre Marca o
          Modelo, al tocar el tope o el final del panel el scroll tiene que
          SEGUIR con la página, como en cualquier barra lateral. Con `contain`
          en todos lados la rueda se moría ahí y la página no se movía. */}
      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain md:overscroll-auto px-1 pb-4">
        {/* `keepContentMounted`: el contenido de cada acordeón se monta UNA vez
            y abrir/cerrar sólo anima alto y opacidad. Sin esto, HeroUI monta y
            desmonta el contenido en cada apertura (Marca: buscador + 10
            checkboxes; Precio: dos inputs y el slider). Las selecciones y el
            estado de Ver más / buscadores ya vivían fuera del acordeón.

            El "parpadeo" al terminar de abrir NO era esto: era framer-motion
            12.16, que al completar una animación acelerada por WAAPI (opacidad,
            transform) dejaba un frame con el valor inicial (`opacity: 0`)
            antes de fijar el final. Está corregido en framer-motion 12.34.5
            (package.json); la animación se conserva. */}
        <Accordion
          selectionMode="single"
          keepContentMounted
          selectedKeys={openAccordion ? [openAccordion] : []}
          onSelectionChange={(keys) => {
            const selectedKey = Array.from(keys)[0]?.toString() || null;
            setOpenAccordion(selectedKey);
          }}
        >
          {/* PRECIO */}
          <AccordionItem
            key="price"
            aria-label={priceLabel}
            startContent={<Icon icon="mdi:cash" className="text-xl" style={CTA ? { color: CTA } : undefined} />}
            title={
              <div className="flex flex-col items-start">
                <div className="font-medium">{priceLabel}</div>
                {(PR[0] > 0 || PR[1] < maxPrice) && (
                  <Chip size="sm" color="primary" variant="flat" className="h-5 mt-1" onClose={resetPrice}>
                    {`${formatCLP(PR[0])} – ${formatCLP(PR[1])}`}
                  </Chip>
                )}
              </div>
            }
            classNames={{
              heading: 'px-2 py-2 hover:bg-white/50 dark:hover:bg-white/5',
              content: 'px-3 py-3',
            }}
          >
            <div className="space-y-3">
              <div className="flex justify-between items-center gap-2">
                <Input
                  type="text"
                  size="sm"
                  inputMode="numeric"
                  placeholder={minPriceText}
                  aria-label={minPriceText}
                  value={minInput}
                  onChange={(e) => setMinInput(formatTecleado(e.target.value))}
                  onBlur={commitMin}
                  onKeyDown={(e) => e.key === 'Enter' && commitMin()}
                  className="w-full min-w-0"
                  // `h-11` = 44px. Los dos campos de precio son la vía precisa
                  // para fijar el rango y con 36px quedaban bajo el mínimo
                  // táctil. 13px en vez de 12: con puntos de miles el número
                  // es más largo y a 12px no se leía. Sin el "$" de prefijo:
                  // en los 272px del panel de /vehicles cada campo tiene ~90px
                  // y "45.000.000" (79px) no entraba con el símbolo y su
                  // separación (medido: el input quedaba en 63px y se cortaba).
                  // La moneda ya está en los rótulos del slider y en el chip.
                  classNames={PRICE_INPUT_CLASSES}
                />
                <span className="text-gray-500 dark:text-gray-400 text-sm shrink-0">–</span>
                <Input
                  type="text"
                  size="sm"
                  inputMode="numeric"
                  placeholder={maxPriceText}
                  aria-label={maxPriceText}
                  value={maxInput}
                  onChange={(e) => setMaxInput(formatTecleado(e.target.value))}
                  onBlur={commitMax}
                  onKeyDown={(e) => e.key === 'Enter' && commitMax()}
                  className="w-full min-w-0"
                  classNames={PRICE_INPUT_CLASSES}
                />
              </div>

              <div className="px-1">
                <Slider
                  value={[PR[0], PR[1]] as [number, number]}
                  onChange={(value) => {
                    const [min, max] = value as [number, number];
                    // Clamp contra maxPrice por si el sitio trae valores out-of-range al reactivarse
                    const nextMin = Math.max(0, Math.min(min, max, maxPrice));
                    const nextMax = Math.max(nextMin, Math.min(max, maxPrice));
                    setPriceRange([nextMin, nextMax]);
                    setMinInput(formatMiles(nextMin));
                    setMaxInput(formatMiles(nextMax));
                  }}
                  minValue={0}
                  maxValue={maxPrice}
                  step={enUsa ? 1_000 : 1_000_000}
                  size="sm"
                  className="max-w-full"
                  classNames={{
                    track: 'dark:bg-white/10',
                    // El thumb se ve de 12px pero su área táctil YA es de 44:
                    // HeroUI le cuelga un `::before` de `w-11 h-11` con
                    // `inset:-16px`. Verificado en producción con hit-test. No
                    // se toca —agregarle un `::after` propio pisaría el que
                    // HeroUI usa para dibujarlo— ni se toca el `step`, que
                    // cambiaría los valores que ya viajan en la URL.
                    thumb: 'dark:border-white/10 h-3 w-3',
                  }}
                  style={CTA ? ({ '--slider-filler-bg': CTA } as React.CSSProperties) : undefined}
                  aria-label={tx('catalog.filters.priceRangeAria', 'Rango de precio')}
                />
                {/* `text-xs` y gris 600/300 en vez de 10px gris 500: los
                    extremos del rango son el dato que se lee para saber dónde
                    quedó el slider, y a 10px eran ilegibles en el celular. */}
                <div className="flex justify-between mt-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 tabular-nums">
                  <span>{formatCLP(PR[0])}</span>
                  <span>{formatCLP(PR[1])}</span>
                </div>
              </div>
            </div>
          </AccordionItem>

          {/* MARCA (checkbox + buscador, 10 de entrada + Ver más) */}
          <AccordionItem
            key="brand"
            aria-label={brandText}
            startContent={<Icon icon="mdi:car-estate" className="text-xl" style={CTA ? { color: CTA } : undefined} />}
            title={<div className="flex items-center gap-2">{brandText}</div>}
            classNames={{ heading: 'px-2 py-2 text-nowrap hover:bg-white/50 dark:hover:bg-white/5', content: 'px-3 py-3' }}
          >
            <Input
              aria-label={enUsa ? 'Search make' : 'Buscar marca'}
              placeholder={enUsa ? 'Search make…' : 'Buscar marca…'}
              size="sm"
              value={brandQuery}
              onChange={(e) => setBrandQuery(e.target.value)}
              className="mb-2"
            />
            <CheckboxGroup
              value={asArray(filters.brand)}
              onChange={(val) => setMulti('brand', (val as string[]) ?? [])}
              className="grid grid-cols-1 gap-1.5"
            >
              {marcas.visibles.map((b) => {
                const id = String(b.id);
                return (
                  <Checkbox
                    key={`brand-${id}`}
                    value={id}
                    classNames={{
                      wrapper: 'rounded-md',
                      label: 'text-sm',
                    }}
                  >
                    {b?.name ?? id}
                  </Checkbox>
                );
              })}
            </CheckboxGroup>
            {marcas.conBoton &&
              botonVerMas({
                expandida: brandExpanded,
                ocultas: marcas.ocultas,
                onToggle: () => setBrandExpanded((v) => !v),
                label: brandText,
              })}

            {asArray(filters.brand).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {asArray(filters.brand).map((id) => (
                  <Chip
                    key={`brand-chip-${id}`}
                    size="sm"
                    variant="flat"
                    color="primary"
                    onClose={() => setMulti('brand', asArray(filters.brand).filter((x) => x !== String(id)))}
                  >
                    {getName(BRANDS, String(id))}
                  </Chip>
                ))}
              </div>
            )}
          </AccordionItem>

          {/* MODELO (checkbox + buscador, dependiente de marca, 10 + Ver más) */}
          <AccordionItem
            key="model"
            aria-label={modelText}
            startContent={<Icon icon="mdi:clipboard-text-outline" className="text-xl" style={CTA ? { color: CTA } : undefined} />}
            title={<div className="flex text-nowrap items-center gap-2">{modelText}</div>}
            classNames={{ heading: 'px-2 py-2 hover:bg-white/50 dark:hover:bg-white/5', content: 'px-3 py-3' }}
          >
            <Input
              aria-label={enUsa ? 'Search model' : 'Buscar modelo'}
              placeholder={enUsa ? 'Search model…' : 'Buscar modelo…'}
              size="sm"
              value={modelQuery}
              onChange={(e) => setModelQuery(e.target.value)}
              className="mb-2"
            />
            <CheckboxGroup
              value={asArray(filters.model)}
              onChange={(val) => setMulti('model', (val as string[]) ?? [])}
              className="grid grid-cols-1 gap-1.5"
            >
              {modelos.visibles.map((m) => {
                const id = String(m.id);
                return (
                  <Checkbox key={`model-${id}`} value={id} classNames={{ wrapper: 'rounded-md', label: 'text-sm' }}>
                    {m?.name ?? id}
                  </Checkbox>
                );
              })}
            </CheckboxGroup>
            {modelos.conBoton &&
              botonVerMas({
                expandida: modelExpanded,
                ocultas: modelos.ocultas,
                onToggle: () => setModelExpanded((v) => !v),
                label: modelText,
              })}

            {asArray(filters.model).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {asArray(filters.model).map((id) => (
                  <Chip
                    key={`model-chip-${id}`}
                    size="sm"
                    variant="flat"
                    color="primary"
                    onClose={() => setMulti('model', asArray(filters.model).filter((x) => x !== String(id)))}
                  >
                    {getName(MODELS, String(id))}
                  </Chip>
                ))}
              </div>
            )}
          </AccordionItem>

          {/* CATEGORÍA (checkbox grid) */}
          <AccordionItem
            key="category"
            aria-label={categoryText}
            startContent={<Icon icon="mdi:car-side" className="text-xl" style={CTA ? { color: CTA } : undefined} />}
            title={<div className="flex  items-center gap-2">{categoryText}</div>}
            classNames={{ heading: 'px-2 py-2 hover:bg-white/50 dark:hover:bg-white/5', content: 'px-3 py-3 text-nowrap' }}
          >
            <CheckboxGroup
              value={asArray(filters.category)}
              onChange={(val) => setMulti('category', (val as string[]) ?? [])}
              className="grid grid-cols-2 gap-1.5"
            >
              {CATEGORIES.map((c) => {
                const id = String(c.id);
                return (
                  <Checkbox key={`cat-${id}`} value={id} classNames={{ wrapper: 'rounded-md', label: 'text-sm' }}>
                    {c?.name != null ? etiqueta('category', c.name) : id}
                  </Checkbox>
                );
              })}
            </CheckboxGroup>

            {asArray(filters.category).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {asArray(filters.category).map((id) => (
                  <Chip
                    key={`cat-chip-${id}`}
                    size="sm"
                    variant="flat"
                    color="primary"
                    onClose={() => setMulti('category', asArray(filters.category).filter((x) => x !== String(id)))}
                  >
                    {etiqueta('category', getName(CATEGORIES, String(id)))}
                  </Chip>
                ))}
              </div>
            )}
          </AccordionItem>


          {/* El cast del cierre: igual que en Facturable y Sucursal, el tipo de
              children del Accordion no admite condicionales, aunque su
              CollectionBuilder descarte los hijos falsy en runtime. */}
          {(hayAlgo(TRANSMISSIONS) && (
            <AccordionItem
              key="transmission"
              aria-label={transmissionText}
              startContent={<Icon icon="mdi:car-shift-pattern" className="text-xl" style={CTA ? { color: CTA } : undefined} />}
              title={<div className="flex items-center gap-2">{transmissionText}</div>}
              classNames={{ heading: 'px-2 py-2 hover:bg-white/50 dark:hover:bg-white/5', content: 'px-3 py-3' }}
            >
              <CheckboxGroup
                value={asArray(filters.transmission)}
                onChange={(val) => setMulti('transmission', (val as string[]) ?? [])}
                className="grid grid-cols-1 gap-1.5"
              >
                {TRANSMISSIONS.map((opt) => (
                  <Checkbox
                    key={`transmission-${opt.id}`}
                    value={opt.id}
                    isDisabled={opt.count === 0}
                    classNames={{ wrapper: 'rounded-md', label: 'text-sm' }}
                  >
                    {SPEC_LABELS[opt.id] ?? opt.id}{' '}
                    <span className="text-gray-500 dark:text-gray-400">({opt.count})</span>
                  </Checkbox>
                ))}
              </CheckboxGroup>

              {asArray(filters.transmission).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {asArray(filters.transmission).map((id) => (
                    <Chip
                      key={`transmission-chip-${id}`}
                      size="sm"
                      variant="flat"
                      onClose={() => setMulti('transmission', asArray(filters.transmission).filter((x) => x !== id))}
                    >
                      {SPEC_LABELS[id] ?? id}
                    </Chip>
                  ))}
                </div>
              )}
            </AccordionItem>
          )) as unknown as JSX.Element}


          {/* El cast del cierre: igual que en Facturable y Sucursal, el tipo de
              children del Accordion no admite condicionales, aunque su
              CollectionBuilder descarte los hijos falsy en runtime. */}
          {(hayAlgo(TRACTIONS) && (
            <AccordionItem
              key="traction"
              aria-label={tractionText}
              startContent={<Icon icon="mdi:car-traction-control" className="text-xl" style={CTA ? { color: CTA } : undefined} />}
              title={<div className="flex items-center gap-2">{tractionText}</div>}
              classNames={{ heading: 'px-2 py-2 hover:bg-white/50 dark:hover:bg-white/5', content: 'px-3 py-3' }}
            >
              <CheckboxGroup
                value={asArray(filters.traction)}
                onChange={(val) => setMulti('traction', (val as string[]) ?? [])}
                className="grid grid-cols-1 gap-1.5"
              >
                {TRACTIONS.map((opt) => (
                  <Checkbox
                    key={`traction-${opt.id}`}
                    value={opt.id}
                    isDisabled={opt.count === 0}
                    classNames={{ wrapper: 'rounded-md', label: 'text-sm' }}
                  >
                    {SPEC_LABELS[opt.id] ?? opt.id}{' '}
                    <span className="text-gray-500 dark:text-gray-400">({opt.count})</span>
                  </Checkbox>
                ))}
              </CheckboxGroup>

              {asArray(filters.traction).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {asArray(filters.traction).map((id) => (
                    <Chip
                      key={`traction-chip-${id}`}
                      size="sm"
                      variant="flat"
                      onClose={() => setMulti('traction', asArray(filters.traction).filter((x) => x !== id))}
                    >
                      {SPEC_LABELS[id] ?? id}
                    </Chip>
                  ))}
                </div>
              )}
            </AccordionItem>
          )) as unknown as JSX.Element}

          {/* FACTURABLE (afecto a IVA). Sólo aparece si el cliente tiene stock:
              con la automotora vacía no hay nada que clasificar. Un tipo sin
              vehículos se muestra deshabilitado en vez de esconderse, para que
              se entienda que la opción existe y hoy no da resultados. */}
          {(conFacturable && (FACTURABLE.si > 0 || FACTURABLE.no > 0) && (
            <AccordionItem
              key="facturable"
              aria-label={facturableText}
              startContent={<Icon icon="mdi:receipt-text-outline" className="text-xl" style={CTA ? { color: CTA } : undefined} />}
              title={<div className="flex items-center gap-2">{facturableText}</div>}
              classNames={{ heading: 'px-2 py-2 hover:bg-white/50 dark:hover:bg-white/5', content: 'px-3 py-3' }}
            >
              <CheckboxGroup
                value={asArray(filters.facturable)}
                onChange={(val) => setMulti('facturable', (val as string[]) ?? [])}
                className="grid grid-cols-1 gap-1.5"
              >
                {FACTURABLE_OPTIONS.map((opt) => (
                  <Checkbox
                    key={`facturable-${opt.id}`}
                    value={opt.id}
                    isDisabled={FACTURABLE[opt.id] === 0}
                    classNames={{ wrapper: 'rounded-md', label: 'text-sm' }}
                  >
                    {opt.label}{' '}
                    <span className="text-gray-500 dark:text-gray-400">({FACTURABLE[opt.id]})</span>
                  </Checkbox>
                ))}
              </CheckboxGroup>

              <p className="mt-2 text-xs leading-snug text-gray-600 dark:text-gray-300">
                {tx(
                  'vehicles.filters.facturableHint',
                  'Los vehículos con factura se venden afectos a IVA, así la empresa puede usar el crédito fiscal.'
                )}
              </p>

              {asArray(filters.facturable).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {asArray(filters.facturable).map((id) => (
                    <Chip
                      key={`facturable-chip-${id}`}
                      size="sm"
                      variant="flat"
                      color="primary"
                      onClose={() => setMulti('facturable', asArray(filters.facturable).filter((x) => x !== String(id)))}
                    >
                      {FACTURABLE_OPTIONS.find((o) => o.id === id)?.label ?? String(id)}
                    </Chip>
                  ))}
                </div>
              )}
            </AccordionItem>
          )) as unknown as JSX.Element}

          {/* SUCURSAL (checkbox; solo si el cliente tiene más de una con stock).
              El cast del final: el tipo de children del Accordion no admite
              condicionales, pero su CollectionBuilder descarta hijos falsy en runtime. */}
          {(DEALERSHIPS.length > 1 && (
            <AccordionItem
              key="dealership"
              aria-label={dealershipText}
              startContent={<Icon icon="mdi:map-marker" className="text-xl" style={CTA ? { color: CTA } : undefined} />}
              title={<div className="flex items-center gap-2">{dealershipText}</div>}
              classNames={{ heading: 'px-2 py-2 hover:bg-white/50 dark:hover:bg-white/5', content: 'px-3 py-3' }}
            >
              <CheckboxGroup
                value={asArray(filters.dealership)}
                onChange={(val) => setMulti('dealership', (val as string[]) ?? [])}
                className="grid grid-cols-1 gap-1.5"
              >
                {DEALERSHIPS.map((d) => {
                  const id = String(d.id);
                  return (
                    <Checkbox key={`dealership-${id}`} value={id} classNames={{ wrapper: 'rounded-md', label: 'text-sm' }}>
                      {d?.name ?? id}
                    </Checkbox>
                  );
                })}
              </CheckboxGroup>

              {asArray(filters.dealership).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {asArray(filters.dealership).map((id) => (
                    <Chip
                      key={`dealership-chip-${id}`}
                      size="sm"
                      variant="flat"
                      color="primary"
                      onClose={() => setMulti('dealership', asArray(filters.dealership).filter((x) => x !== String(id)))}
                    >
                      {getName(DEALERSHIPS, String(id))}
                    </Chip>
                  ))}
                </div>
              )}
            </AccordionItem>
          )) as unknown as JSX.Element}

          {/* COMBUSTIBLE (checkbox grid) */}
          <AccordionItem
            key="fuel_type"
            aria-label={fuelText}
            startContent={<Icon icon="mdi:gas-station" className="text-xl" style={CTA ? { color: CTA } : undefined} />}
            title={<div className="flex items-center gap-2">{fuelText}</div>}
            classNames={{ heading: 'px-2 py-2 hover:bg-white/50 dark:hover:bg-white/5', content: 'px-3 py-3' }}
          >
            <CheckboxGroup
              value={asArray(filters.fuel_type)}
              onChange={(val) => setMulti('fuel_type', (val as string[]) ?? [])}
              className="grid grid-cols-2 gap-1.5"
            >
              {FUELTYPES.map((f) => {
                const id = String(f.id);
                return (
                  <Checkbox key={`fuel-${id}`} value={id} classNames={{ wrapper: 'rounded-md', label: 'text-sm' }}>
                    {f?.name != null ? etiqueta('fuel', f.name) : id}
                  </Checkbox>
                );
              })}
            </CheckboxGroup>

            {asArray(filters.fuel_type).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {asArray(filters.fuel_type).map((id) => (
                  <Chip
                    key={`fuel-chip-${id}`}
                    size="sm"
                    variant="flat"
                    color="primary"
                    onClose={() => setMulti('fuel_type', asArray(filters.fuel_type).filter((x) => x !== String(id)))}
                  >
                    {etiqueta('fuel', getName(FUELTYPES, String(id)))}
                  </Chip>
                ))}
              </div>
            )}
          </AccordionItem>

          {/* AÑO (rango Desde / Hasta) */}
          <AccordionItem
            key="year"
            aria-label={yearText}
            startContent={<Icon icon="mdi:calendar" className="text-xl" style={CTA ? { color: CTA } : undefined} />}
            title={<div className="flex items-center gap-2">{yearText}</div>}
            classNames={{ heading: 'px-2 py-2 hover:bg-white/50 dark:hover:bg-white/5', content: 'px-3 py-3' }}
          >
            <div className="flex items-end gap-2">
              <label className="flex-1 min-w-0">
                <span className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">{yearFromText}</span>
                <select
                  value={yearFrom ?? ''}
                  onChange={(e) => setYearRange(e.target.value ? Number(e.target.value) : null, yearTo)}
                  aria-label={`${yearText} ${yearFromText}`}
                  className={YEAR_SELECT_CLASS}
                >
                  <option value="">{anyYearText}</option>
                  {YEAR_OPTIONS.map((y) => (
                    <option key={`from-${y}`} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </label>

              <span className="pb-3 text-gray-500 dark:text-gray-400">–</span>

              <label className="flex-1 min-w-0">
                <span className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">{yearToText}</span>
                <select
                  value={yearTo ?? ''}
                  onChange={(e) => setYearRange(yearFrom, e.target.value ? Number(e.target.value) : null)}
                  aria-label={`${yearText} ${yearToText}`}
                  className={YEAR_SELECT_CLASS}
                >
                  <option value="">{anyYearText}</option>
                  {YEAR_OPTIONS.map((y) => (
                    <option key={`to-${y}`} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {(yearFrom !== null || yearTo !== null) && (
              <div className="mt-3 flex items-center justify-between gap-2">
                <Chip size="sm" variant="flat" color="primary" onClose={() => setMulti('year', [])}>
                  {yearFrom !== null && yearTo !== null && yearFrom !== yearTo
                    ? `${yearFrom} – ${yearTo}`
                    : yearFrom !== null && yearTo !== null
                      ? `${yearFrom}`
                      : yearFrom !== null
                        ? `${yearFromText} ${yearFrom}`
                        : `${yearToText} ${yearTo}`}
                </Chip>
              </div>
            )}
          </AccordionItem>

          {/* CONDICIÓN (checkbox grid) */}
          <AccordionItem
            key="condition"
            aria-label={conditionText}
            startContent={<Icon icon="mdi:car-info" className="text-xl" style={CTA ? { color: CTA } : undefined} />}
            title={<div className="flex items-center gap-2">{conditionText}</div>}
            classNames={{ heading: 'px-2 py-2 hover:bg-white/50 dark:hover:bg-white/5', content: 'px-3 py-3 text-nowrap' }}
          >
            <CheckboxGroup
              value={asArray(filters.condition)}
              onChange={(val) => setMulti('condition', (val as string[]) ?? [])}
              className="grid grid-cols-2 gap-1.5"
            >
              {CONDITIONS.map((c) => {
                const id = String(c.id);
                return (
                  <Checkbox key={`cond-${id}`} value={id} classNames={{ wrapper: 'rounded-md', label: 'text-sm' }}>
                    {c?.name != null ? etiqueta('condition', c.name) : id}
                  </Checkbox>
                );
              })}
            </CheckboxGroup>

            {asArray(filters.condition).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {asArray(filters.condition).map((id) => (
                  <Chip
                    key={`cond-chip-${id}`}
                    size="sm"
                    variant="flat"
                    color="primary"
                    onClose={() => setMulti('condition', asArray(filters.condition).filter((x) => x !== String(id)))}
                  >
                    {etiqueta('condition', getName(CONDITIONS, String(id)))}
                  </Chip>
                ))}
              </div>
            )}
          </AccordionItem>

          {/* COLOR (checkbox con swatch) */}
          <AccordionItem
            key="color"
            aria-label={colorText}
            startContent={<Icon icon="mdi:palette" className="text-xl" style={CTA ? { color: CTA } : undefined} />}
            title={<div className="flex items-center gap-2">{colorText}</div>}
            classNames={{ heading: 'px-2 py-2 hover:bg-white/50 dark:hover:bg-white/5', content: 'px-3 py-3 ' }}
          >
            <CheckboxGroup
              value={asArray(filters.color)}
              onChange={(val) => setMulti('color', (val as string[]) ?? [])}
              className="grid grid-cols-2 gap-1.5"
            >
              {COLORS.map((c) => {
                const id = String(c.id);
                return (
                  <Checkbox
                    key={`color-${id}`}
                    value={id}
                    classNames={{ wrapper: 'rounded-md', label: 'text-sm flex items-center gap-2' }}
                  >
                    <span
                      className="inline-block w-3.5 h-3.5 rounded-full border border-white/20"
                      style={{ backgroundColor: c?.hex }}
                    />
                    {c?.name != null ? etiqueta('color', c.name) : id}
                  </Checkbox>
                );
              })}
            </CheckboxGroup>

            {asArray(filters.color).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {asArray(filters.color).map((id) => (
                  <Chip
                    key={`color-chip-${id}`}
                    size="sm"
                    variant="flat"
                    color="primary"
                    onClose={() => setMulti('color', asArray(filters.color).filter((x) => x !== String(id)))}
                  >
                    {etiqueta('color', getName(COLORS, String(id)))}
                  </Chip>
                ))}
              </div>
            )}
          </AccordionItem>
        </Accordion>
      </div>
    </div>
  );
};

export default NewVehicleFilters;
